import { MenuAlt2Icon, MenuIcon } from '@heroicons/react/outline';
import { useRouter } from 'next/router';
import useLocale from 'next-translate/useTranslation';
import { memo, useState } from 'react';
import * as React from 'react';

import clsxm from '@/lib/clsxm';
import { useDashboardChromePresence } from '@/lib/siteChatVisibility';

import { useUser } from '@/utils/useUser';

import MobileTabBar, { MobileTabBarSpacer } from './MobileTabBar';
import MobileNavDrawer from './nav/MobileNavDrawer';
import NavRail from './nav/NavRail';
import { nav } from './nav/navUi';

function NarrowDashboardLayout({
  children,
  sidebarVisible = true,
  showMobileMenu = false,
  tone = 'default',
  hideSidebar = false,
  mobileTabBar = false,
}: {
  children: React.ReactNode;
  sidebarVisible?: boolean;
  showMobileMenu?: boolean;
  /** No desktop rail: the page brings its own sidebar (like /chat). */
  hideSidebar?: boolean;
  /**
   * 'dark' for pages that are always dark (the video editor gallery): the
   * rail and the phone menu button are drawn dark whatever the theme, or a
   * white rail stands next to a black page in light mode.
   */
  tone?: 'default' | 'dark';
  /**
   * The phone tab bar (see DashboardLayout). Off by default: most pages here
   * are editors, chats or generators with their own bottom UI.
   */
  mobileTabBar?: boolean;
}) {
  // Tells the site-wide support bubble it is inside the product (see
  // SiteChatWidget); BotDashboardLayout wraps this component, so it's covered.
  useDashboardChromePresence();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { t } = useLocale('dashboard');
  const router = useRouter();
  const showRail = !hideSidebar && router.pathname !== '/chat';
  const { user, isLoading } = useUser();
  const showTabBar = mobileTabBar && (!!user || isLoading);
  const floatingMenu = showMobileMenu && tone === 'default';

  return (
    <>
      {/* translate='no': Chrome auto-translate breaks React reconciliation
          (see DashboardLayout) — the app ships 13 locales natively. */}
      <div
        translate='no'
        className={clsxm(
          'notranslate flex h-dvh overflow-hidden sm:h-screen',
          // The floating menu button covers the page's top-start corner
          // below lg; ToolsNav moves its category row past it with this.
          floatingMenu && 'max-lg:[--floating-menu-offset:3.25rem]',
          floatingMenu && showTabBar && 'max-md:[--floating-menu-offset:0px]',
        )}
      >
        <MobileNavDrawer
          open={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          className='lg:hidden'
          closeFrom={1024}
        />

        {/* Static rail for desktop */}
        {showRail && (
          // `dark` scopes Tailwind's dark: styles to the rail.
          <div
            className={clsxm(
              'hidden lg:flex lg:shrink-0',
              tone === 'dark' && 'dark',
            )}
          >
            <div
              // Collapsed, its links are out of view: keep them out of the
              // tab order too.
              inert={!sidebarVisible}
              className={clsxm(
                // 80 px: the generator pages pin their left panel at
                // `lg:ml-20` / `lg:left-20` to clear it.
                sidebarVisible
                  ? 'w-20 translate-x-0'
                  : 'w-0 -translate-x-20 rtl:translate-x-20',
                'relative flex transform overflow-hidden transition-all duration-300 ease-in-out',
              )}
            >
              <NavRail
                className={
                  // The editor's own panel colour, not the app's slate.
                  tone === 'dark'
                    ? 'dark:border-white/[0.06] dark:bg-ve-panel'
                    : undefined
                }
              />
            </div>
          </div>
        )}

        <div className='flex min-w-0 flex-1 flex-col overflow-hidden'>
          {/* Mobile top navigation */}
          {showMobileMenu && tone === 'dark' && (
            <div className='absolute left-3 top-[1.4rem] z-50 lg:hidden lg:top-3'>
              <button
                type='button'
                aria-haspopup='dialog'
                aria-expanded={mobileMenuOpen}
                className='inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white/[0.06] text-zinc-300 ring-1 ring-inset ring-white/10 transition-colors hover:bg-white/[0.1] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400'
                onClick={() => setMobileMenuOpen(true)}
              >
                <span className='sr-only'>{t('Menu')}</span>
                <MenuAlt2Icon className='h-5 w-5' aria-hidden='true' />
              </button>
            </div>
          )}
          {floatingMenu && (
            // On phones the tab bar's Menu opens the same drawer.
            <div
              className={clsxm(
                'absolute start-3 top-3 z-40 lg:hidden',
                showTabBar && 'max-md:hidden',
              )}
            >
              <button
                type='button'
                aria-haspopup='dialog'
                aria-expanded={mobileMenuOpen}
                onClick={() => setMobileMenuOpen(true)}
                className={clsxm(
                  'flex h-10 w-10 items-center justify-center rounded-xl bg-white/95 shadow-[0_1px_2px_rgba(16,16,40,0.06)] ring-1 ring-[#0f0f28]/[0.08] backdrop-blur transition-colors hover:text-[#10101c] dark:bg-slate-800/95 dark:ring-white/10 dark:hover:text-white',
                  nav.body,
                  nav.focus,
                )}
              >
                <span className='sr-only'>{t('Menu')}</span>
                <MenuIcon className='h-5 w-5' aria-hidden='true' />
              </button>
            </div>
          )}

          {children}
          {showTabBar && <MobileTabBarSpacer />}
        </div>
      </div>

      {showTabBar && (
        <MobileTabBar
          onMenu={() => setMobileMenuOpen(true)}
          menuOpen={mobileMenuOpen}
        />
      )}
    </>
  );
}

//memoized export
export default memo(NarrowDashboardLayout);
